import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toggleModal } from '../../../../reducers/modalReducer';
import * as ROUTES from '../../../../constants/routes';

// Tabs carry a `value` and a `label`. The value is the internal id used for hash routing, for the
// `companyDashboardTab == 'x'` render conditions, and by the ~26 setCompanyDashboardTab call sites
// across the app — so renaming a tab is a label-only change and never breaks navigation or old
// bookmarked #hash links.
const tab = (value, label = value) => ({ value, label });

// Row 1 — the main dashboard plus the co-branded partner destinations.
export const PARTNER_TABS = [tab('Financial'), tab('Technology')];
export const PRIMARY_TABS = [tab('Dashboard', 'Corporate'), ...PARTNER_TABS];
// Row 2 — the module tabs, which belong to the main dashboard tab.
export const MODULE_TABS = ['Analyser', 'AnyLists', 'Workflows', 'ResearchAid', 'InvestorMatch', 'AppStore'].map(t => tab(t));

const PARTNER_VALUES = PARTNER_TABS.map(t => t.value);
// Tabs the dashboard can route to via the URL hash. Partner tabs are hover-only for now — they have
// no page behind them, so they are deliberately not routable.
export const ALL_TABS = ['Dashboard', ...MODULE_TABS.map(t => t.value)];

/**
 * Reusable company sub-navigation tabs.
 *
 * Rendered as two rows: the main dashboard tab + partner tabs on top, module tabs beneath. The
 * module row is hidden by default and revealed while a partner tab is hovered.
 *
 * @param {string}   companyId       - Current company id (used when navigating)
 * @param {string}   [activeTab]     - Currently active tab *value* (highlights it). Omit for no highlight.
 * @param {function} [onTabChange]   - Called with the tab *value* when clicked (inline mode)
 * @param {boolean}  [navigateOnClick] - If true, clicking navigates to /company-old/:id instead of calling onTabChange
 * @param {string}   [className]     - Extra wrapper class names
 */
export default function CompanyTabs({ company, companyId, activeTab, onTabChange, navigateOnClick = false, className = '', }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(state => state.user);
    const [hoveredPartner, setHoveredPartner] = useState('');
    // Closing is deferred so the pointer can travel from a partner tab down into the module row
    // without the row vanishing en route. Any re-entry cancels the pending close.
    const closeTimer = useRef(null);
    const HOVER_CLOSE_DELAY = 300;

    const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
    const openRow = (item) => { cancelClose(); setHoveredPartner(item); };
    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setHoveredPartner(''), HOVER_CLOSE_DELAY);
    };

    useEffect(() => cancelClose, []);

    if (user?.['type'] === 'Superflow' || user?.['type'] === 'External') return null;

    const isGuest = user?.['role'] === 'Guest';
    const hideInvestorMatch = user?.['role'] === 'Advisor' || user?.['role'] === 'Investor' || company?.['testType'] == "growth" || company?.['testType'] == "exit";

    // The module row is revealed by hovering a partner tab, and hidden otherwise.
    const showModuleRow = !!hoveredPartner;

    const handleClick = (item) => {
        if (item === 'InvestorMatch') {
            if (navigateOnClick) {
                dispatch(toggleModal({ open: 'investorMatchDisclaimer' }));
                return;
            }
            if (activeTab !== item) {
                dispatch(toggleModal({ open: 'investorMatchDisclaimer' }));
                return;
            }
        }

        if (navigateOnClick) {
            navigate(`/company/${companyId}`, { state: { activeTab: item } });
        } else if (onTabChange) {
            onTabChange(item);
        }
    };

    const renderTab = ({ value, label }, idx, isPartner = false) => (
        <div key={`company-tab-${value}-${idx}`} className="df fdc">
            <button
                // Reason: partner tabs must stay enabled — a `disabled` button fires no mouse events in
                // most browsers, which would kill the hover that reveals the module row.
                disabled={isGuest}
                className={`fs1-25rem fw500 ${isPartner ? 'c3 cp' : 'c15'}${activeTab === value ? ' active' : ''}`}
                // Partner tabs open the co-branded page in a new browser tab rather than changing tab.
                onClick={isPartner ? () => window.open(ROUTES.MORSEBRIDGE, '_blank', 'noopener,noreferrer') : () => handleClick(value)}
                // Partner tabs open the row; module tabs must only hold it open — clearing it here is what
                // made the row vanish the moment the pointer reached a module tab. Focus mirrors hover.
                onMouseEnter={isPartner ? () => openRow(value) : cancelClose}
                onFocus={isPartner ? () => openRow(value) : cancelClose}
                // Guests keep the not-allowed cursor; partner tabs take it from the `cp` utility class.
                style={{ cursor: isGuest ? 'not-allowed' : isPartner ? undefined : 'pointer' }}
            >
                {label}
            </button>
        </div>
    );

    return (
        <div className={`company-tabs-wrap ${className}`.trim()} onMouseLeave={scheduleClose} onMouseEnter={cancelClose}>
            <div className="tabs-navlist tabs-dashboard df aic jcc mt2rem">
                {PRIMARY_TABS.map((item, idx) => renderTab(item, idx, PARTNER_VALUES.includes(item.value)))}
            </div>

            {showModuleRow && (
                <div className="tabs-navlist tabs-dashboard company-tabs-modules df aic jcc" onMouseEnter={cancelClose}>
                    {MODULE_TABS.map((item, idx) => {
                        if (hideInvestorMatch && item.value === 'InvestorMatch') return null;
                        return renderTab(item, idx);
                    })}
                </div>
            )}
        </div>
    );
}
